import { Check } from "@mui/icons-material";
import {
  Avatar,
  ListItemIcon,
  ListItemText,
  Typography,
  alpha,
} from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { stringAvatar } from "utils";

export type ChildPickerChild = {
  firstName: string;
  lastName: string;
  photo?: string | File | null;
  age?: number | null;
};

type ChildAvatarProps = {
  child: Pick<ChildPickerChild, "firstName" | "lastName" | "photo">;
  size?: number;
  fontSize?: number;
  bordered?: boolean;
};

export const ChildAvatar: React.FC<ChildAvatarProps> = ({
  child,
  size = 32,
  fontSize = 13,
  bordered = false,
}) => {
  const displayName = `${child.firstName} ${child.lastName}`;
  const borderSx = bordered
    ? { border: 2, borderColor: "primary.main" as const }
    : {};

  if (typeof child.photo === "string" && child.photo) {
    return (
      <Avatar
        src={child.photo}
        sx={{ width: size, height: size, ...borderSx }}
      />
    );
  }
  return (
    <Avatar
      {...stringAvatar(displayName, {
        width: size,
        height: size,
        fontSize,
        ...borderSx,
      })}
    />
  );
};

export const childPickerMenuItemSx = (isSelected: boolean) => ({
  borderRadius: 1.5,
  mx: 0.75,
  my: 0.25,
  py: 1,
  ...(isSelected && {
    bgcolor: (theme: Theme) => alpha(theme.palette.primary.main, 0.08),
    "&:hover": {
      bgcolor: (theme: Theme) => alpha(theme.palette.primary.main, 0.14),
    },
  }),
});

export const childPickerMenuProps = {
  PaperProps: {
    sx: {
      borderRadius: 2.5,
      mt: 1,
      boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
      py: 0.5,
    },
  },
} as const;

type ChildPickerOptionProps = {
  child: ChildPickerChild;
  isSelected: boolean;
};

const ChildPickerOption: React.FC<ChildPickerOptionProps> = ({
  child,
  isSelected,
}) => {
  const { t } = useTranslation();
  const displayName = `${child.firstName} ${child.lastName}`;
  const ageText =
    child.age != null
      ? `${child.age} ${t(child.age === 1 ? "labels.year_age" : "labels.years_age")}`
      : "";

  return (
    <>
      <ListItemIcon sx={{ minWidth: 44 }}>
        <ChildAvatar child={child} bordered={isSelected} />
      </ListItemIcon>
      <ListItemText
        primary={
          <Typography variant="body2" fontWeight={isSelected ? 600 : 400}>
            {displayName}
          </Typography>
        }
        secondary={ageText || undefined}
        secondaryTypographyProps={{
          variant: "caption",
          color: "text.disabled",
        }}
      />
      {isSelected && (
        <Check sx={{ fontSize: 18, color: "primary.main", ml: 1 }} />
      )}
    </>
  );
};

export default ChildPickerOption;
